import {
    CLOUD_TYPE,
    isAzure,
    isAWS,
    isGCP,
    isVSphere,
    VSPHERE_TYPES,
    isAzureLocal,
    isDownloadableCloudType,
} from '../../../../constants';
import { CONFIG } from '../../../../constants';
import { getCloudAccountAuthType, removeTrailingChars } from '../../../../lib/helpers';

export const getExternalId = () => {
    const currentOrg = JSON.parse(sessionStorage.getItem('currentOrg') || '{}');
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    return currentOrg.external_id || user.external_id || '';
};

export const getPrivateDeploymentCloud = () => {
    return JSON.parse(sessionStorage.getItem('private_deployment') || '{}').cloud || '';
};

const normalizeUrl = (cloudType, url) => {
    if (isDownloadableCloudType(cloudType)) {
        return 'n/a';
    }
    return url ? removeTrailingChars(url, '/')?.trim() : '';
};

/**
 * Build payload for cloud account update
 */
export const buildCAPayloadForEdit = (cloudType, formData, userOrg, tags, additionalData = {}) => {
    const payload = {
        type: cloudType,
    };
    const private_deployment_cloud = getPrivateDeploymentCloud();
    const authType = getCloudAccountAuthType(cloudType);
    const originalData = additionalData.originalData || {};

    if (isDownloadableCloudType(cloudType) && additionalData.token) {
        payload.token = additionalData.token;
    }

    const oldTags = originalData.tags || [];
    if (tags && JSON.stringify([...tags].sort()) !== JSON.stringify([...oldTags].sort())) {
        payload.tags = tags;
    }

    if (formData.vsphere_version && formData.vsphere_version !== originalData.vsphere_version) {
        payload.vsphere_version = formData.vsphere_version;
    }

    if (formData.url && formData.url !== originalData.url) {
        payload.url = removeTrailingChars(formData.url, '/').trim();
    }

    if (formData.name && formData.name !== originalData.name) {
        payload.name = formData.name.trim();
    }

    if (isAzure(cloudType) || isAzureLocal(cloudType)) {
        if (formData.tenant_id && formData.tenant_id !== originalData.tenant_id) {
            payload.tenant_id = formData.tenant_id.trim();
        }
    }

    // Auth - only if credentials are being changed
    if (formData.username || formData.password) {
        if (isGCP(cloudType)) {
            if (formData.username) {
                payload.auth = {
                    auth_type: authType,
                    credentials: {
                        role_arn: formData.username?.trim(),
                    },
                };
            }
        } else if (isAzure(cloudType) || isAzureLocal(cloudType)) {
            if (formData.username && formData.password) {
                payload.auth = {
                    auth_type: authType,
                    credentials: {
                        identity: formData.username?.trim(),
                        secret: formData.password?.trim(),
                    },
                };
            }
        } else if (isAWS(cloudType)) {
            let awsAuthType = authType;
            if (isAWS(private_deployment_cloud)) {
                awsAuthType = 'attached_role';
            }
            // AWS uses role_arn
            if (formData.username) {
                payload.auth = {
                    auth_type: awsAuthType,
                    credentials: {
                        role_arn: formData.username,
                        external_id: getExternalId(),
                    },
                };
            }
        }
    }

    if (additionalData.vsphereCredentials) {
        const { username, password } = additionalData.vsphereCredentials;
        if (username && password) {
            payload.source_clouds = {
                vsphere: {
                    auth: {
                        auth_type: 'password',
                        credentials: {
                            identity: username,
                            secret: password,
                        },
                    },
                },
            };
        }
    }

    return payload;
};

/**
 * Build payload for cloud account creation
 */
export const buildCAPayloadForAdd = (cloudType, formData, userOrg, tags, additionalData = {}) => {
    const payload = {
        type: cloudType,
    };
    const private_deployment_cloud = getPrivateDeploymentCloud();
    let authType = getCloudAccountAuthType(cloudType);
    let credentials = {};
    payload.name = formData.name.trim();
    payload.organization_id = userOrg;
    payload.tenant_id = 'n/a';
    payload.url = normalizeUrl(cloudType, formData.url);

    if (isDownloadableCloudType(cloudType)) {
        payload.appliance_id = additionalData.applianceId;
        payload.token = additionalData.token;
    }

    if (isVSphere(cloudType)) {
        payload.vsphere_version = formData.vsphere_version;
    }

    if (tags && tags.length > 0) {
        payload.tags = tags;
    }

    if (isAzure(cloudType) || isAzureLocal(cloudType)) {
        payload.tenant_id = formData?.tenant_id?.trim();
        credentials = {
            identity: formData.username || '',
            secret: formData.password || '',
        };
    } else if (isAWS(cloudType) || isGCP(cloudType)) {
        if (isAWS(cloudType) && isAWS(private_deployment_cloud)) {
            payload.name = payload.name || 'test';
            authType = 'attached_role';
            credentials = {
                role_arn: '',
                external_id: '',
            };
        } else {
            credentials = {
                role_arn: formData.username || '',
                external_id: isAWS(cloudType) ? getExternalId() : formData.password,
            };
        }
    } else {
        credentials = {
            identity: formData.username || '',
            secret: formData.password || '',
        };
    }

    if (cloudType === VSPHERE_TYPES.VSPHERE_DR || cloudType === VSPHERE_TYPES.VSPHERE_OFFLINE_REPLICAS) {
        payload.auth = null;
    } else {
        payload.auth = {
            auth_type: authType,
            credentials: credentials,
        };
    }

    if (additionalData.drEnabled) {
        payload.dr_enabled = true;
        payload.vsphere_version = 'ESXi_6_7_U3';
    }

    return payload;
};

/**
 * Build payload for connection test
 */
export const buildTestConnectionPayload = (cloudType, formData, userOrg) => {
    const testPayload = {
        type: cloudType,
        name: formData.name.trim() || '',
        organization_id: userOrg,
    };
    testPayload.tenant_id = 'n/a';
    let authType = getCloudAccountAuthType(cloudType);
    let credentials = {};
    testPayload.url = normalizeUrl(cloudType, formData.url);

    if ((isAzure(cloudType) || isAzureLocal(cloudType)) && formData.tenant_id) {
        testPayload.tenant_id = formData.tenant_id;
    }

    if (isVSphere(cloudType) && formData.vsphere_version) {
        testPayload.vsphere_version = formData.vsphere_version;
    }

    if (isAzure(cloudType) || isAzureLocal(cloudType)) {
        credentials = {
            identity: formData.username || '',
            secret: formData.password || '',
        };
    } else if (isAWS(cloudType) || isGCP(cloudType)) {
        const private_deployment_cloud = getPrivateDeploymentCloud();
        if (isAWS(cloudType) && isAWS(private_deployment_cloud)) {
            authType = 'attached_role';
            credentials = {
                role_arn: '',
                external_id: '',
            };
        } else {
            const externalId = isAWS(cloudType) ? getExternalId() : formData.password;
            credentials = {
                role_arn: formData.username || '',
                external_id: externalId || '',
            };
        }
    } else {
        credentials = {
            identity: formData.username || '',
            secret: formData.password || '',
        };
    }

    testPayload.auth = {
        auth_type: authType,
        credentials: credentials,
    };

    return testPayload;
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Get help documentation link
 */
export const getHelpLink = (linkKey) => {
    return CONFIG?.[linkKey] || 'http://docs.rivermeadow.com/getting-started';
};

/**
 * Get documentation link and label for downloadable cloud types
 * Returns object with { label, linkText, url }
 */
export const getDownloadableDocumentation = (cloudType) => {
    const documentationMap = {
        [CLOUD_TYPE.VSPHERE]: {
            label: 'Install appliance to source vCenter',
            linkText: 'Documentation: vSphere',
            url: getHelpLink('VSPHERE_APPLIANCE_DOCS'),
        },
        [CLOUD_TYPE.OPENSHIFT]: {
            label: 'Create Image and deploy to OpenShift',
            linkText: 'Documentation: OpenShift',
            url: getHelpLink('OPENSHIFT_APPLIANCE_DOCS'),
        },
        [CLOUD_TYPE.OPENSTACK]: {
            label: 'Create Image and deploy to OpenStack',
            linkText: 'Documentation: OpenStack',
            url: getHelpLink('OPENSTACK_APPLIANCE_DOCS'),
        },
        [CLOUD_TYPE.NC2]: {
            label: 'Create Image and deploy to Nutanix NC2',
            linkText: 'Documentation: Nutanix NC2',
            url: getHelpLink('NC2_APPLIANCE_DOCS'),
        },
        [CLOUD_TYPE.HPE_VME]: {
            label: 'Create Image and deploy to HPE VM Essentials',
            linkText: 'Documentation: HPE VM Essentials',
            url: getHelpLink('HPE_VME_APPLIANCE_DOCS'),
        },
        [CLOUD_TYPE.HYPERV]: {
            label: 'Create Image and deploy to Hyper-V',
            linkText: 'Documentation: Hyper-V',
            url: getHelpLink('HYPERV_APPLIANCE_DOCS'),
        },
    };

    return documentationMap[cloudType] || null;
};
